import React, { useState, useRef, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageTracking } from './hooks/usePageTracking';
import { useDocumentMeta } from './hooks/useDocumentMeta';
import './App.css';

// Auth & Subscription
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FreeLimitProvider } from './contexts/FreeLimitContext';
import { TokenProvider, useTokens } from './contexts/TokenContext';
import { LevelUpProvider } from './components/progression';

import {
  LoginPage,
  RegisterPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  VerifyEmailPage,
  ProfilePage,
  AuthCallbackPage,
  ParentalConsentPage,
} from './pages/auth';
import ParentalControlsPage from './pages/account/ParentalControlsPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AuthBootstrapGate from './components/auth/AuthBootstrapGate';
import EmailVerificationBanner from './components/auth/EmailVerificationBanner';

// Layout Components
import Footer from './components/layout/Footer';
import CookieConsent from './components/layout/CookieConsent';
import ScrollToTop from './components/layout/ScrollToTop';

// Notification Components
import { NotificationBell } from './components/notifications';
import { useNotificationStore } from './stores/notificationStore';

// Pages
import HomePage from './pages/HomePage';
import PricingPage from './pages/PricingPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import { TermsPage, PrivacyPage } from './pages/legal';
import CreatorWizardPage from './pages/CreatorWizardPage';
import CreatorCoursesPage from './pages/CreatorCoursesPage';
import CreatorGroupsPage from './pages/CreatorGroupsPage';
import DictionaryPage from './pages/DictionaryPage';
import LearningAPITestPage from './pages/LearningAPITestPage';
import FullTextTestPage from './pages/FullTextTestPage';
import SubtitleEditorPage from './pages/SubtitleEditorPage';
import CourseListPage from './pages/CourseListPage';
import TopicsPage from './pages/TopicsPage';
import CourseDetailPage from './pages/CourseDetailPage';
import MyCoursesPage from './pages/MyCoursesPage';
import LearningLayout from './pages/learning/LearningLayout';
import ComprehensionPage from './pages/learning/ComprehensionPage';
import VocabularyPage from './pages/learning/VocabularyPage';
import DictationPage from './pages/learning/DictationPage';
import ShadowingPage from './pages/learning/ShadowingPage';
import QuizPage from './pages/learning/QuizPage';
import SubscriptionGate from './components/subscription/SubscriptionGate';
import TieredGate from './components/subscription/TieredGate';
import WelcomeTrialModal from './components/onboarding/WelcomeTrialModal';
import { LoopyMark } from './components/loopy';
import NotesPage from './pages/NotesPage';
import VocabularyBookPage from './pages/VocabularyBookPage';
import FlashcardPracticePage from './pages/FlashcardPracticePage';
import ReviewCenterPage from './pages/ReviewCenter/ReviewCenterPage';
import TokensPage from './pages/TokensPage';
import UnderlineTestPage from './pages/UnderlineTestPage';
import TranslationEvalTestPage from './pages/TranslationEvalTestPage';
import ModelComparePage from './pages/test/ModelComparePage';
import TTSComparePage from './pages/test/TTSComparePage';
import TTSTemperaturePage from './pages/test/TTSTemperaturePage';
import SilenceExperimentPage from './pages/test/SilenceExperimentPage';
import IntonationComparePage from './pages/test/IntonationComparePage';
import EpisodeSelectorTestPage from './pages/test/EpisodeSelectorTestPage';
import SpeechInputTestPage from './pages/test/SpeechInputTestPage';
import ShadowingExperimentPage from './pages/test/ShadowingExperimentPage';
import AnnotationParserTestPage from './pages/test/AnnotationParserTestPage';
import CEFRTreePage from './pages/test/CEFRTreePage';
import LearningJourneyPage from './pages/test/LearningJourneyPage';
import SyntaxComparePage from './pages/test/SyntaxComparePage';
import AsrComparePage from './pages/test/AsrComparePage';
import SyntaxTreeDemoPage from './pages/SyntaxTreeDemoPage';
import PlacementTestPage from './pages/PlacementTestPage';
import PlacementTestHistoryPage from './pages/PlacementTestHistoryPage';
import PlacementTestResultPage from './pages/PlacementTestResultPage';

// Writing Pages (formerly Review Quest)
import { CourseWritingPage, GatePage, WritingPage, StandaloneGatePage, WritingHistoryPage, WritingHistoryDetailPage } from './pages/writing';
import CourseWritingQuestionsPage from './pages/CourseWritingQuestionsPage';

// Progression Pages
import ProgressionPreview from './pages/progression/ProgressionPreview';
import ProgressionPage from './pages/ProgressionPage';
import KnowledgeGraphPage from './pages/KnowledgeGraphPage';
import CaffaExplainerPage from './pages/CaffaExplainerPage';
import LearningReportsListPage from './pages/LearningReportsListPage';
import LearningReportDetailPage from './pages/LearningReportDetailPage';
import PhonicsReferencePage from './pages/PhonicsReferencePage';
import MorphologyReferencePage from './pages/MorphologyReferencePage';
import ProsodyReferencePage from './pages/ProsodyReferencePage';
import SightWordsReferencePage from './pages/SightWordsReferencePage';
import CEFRGuidePage from './pages/CEFRGuidePage';

// Playlist & Series Pages
import MyPlaylistsPage from './pages/MyPlaylistsPage';
import PlaylistDetailPage from './pages/PlaylistDetailPage';
import MySeriesPage from './pages/MySeriesPage';
import SeriesDetailPage from './pages/SeriesDetailPage';

// About & Blog Pages
import AboutPage from './pages/AboutPage';
import FaqPage from './pages/FaqPage';
import BlogListPage from './pages/BlogListPage';
import BlogDetailPage from './pages/BlogDetailPage';
import ContactPage from './pages/ContactPage';

// Wishlist Page
import WishlistPage from './pages/WishlistPage';

// Referral Page
import ReferralPage from './pages/ReferralPage';
import WelcomePage from './pages/WelcomePage';

// Messaging & Profile Pages
import MessagesPage from './pages/MessagesPage';
import NotificationsPage from './pages/NotificationsPage';
import PublicProfilePage from './pages/PublicProfilePage';

// Admin Pages
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminSubscriptionsPage from './pages/admin/AdminSubscriptionsPage';
import AdminSubscriptionDetailPage from './pages/admin/AdminSubscriptionDetailPage';
import AdminTransactionsPage from './pages/admin/AdminTransactionsPage';
import AdminRevenuePage from './pages/admin/AdminRevenuePage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminUserDetailPage from './pages/admin/AdminUserDetailPage';
import AdminChurnPage from './pages/admin/AdminChurnPage';
import AdminLearningTimePage from './pages/admin/AdminLearningTimePage';
import AdminCreatorContributionsPage from './pages/admin/AdminCreatorContributionsPage';
import AdminAuditLogPage from './pages/admin/AdminAuditLogPage';
import AdminEmailHubPage from './pages/AdminEmailHubPage';

// Creator Analytics
import CreatorAnalyticsPage from './pages/CreatorAnalyticsPage';
import CreatorLearnerQuestionsPage from './pages/CreatorLearnerQuestionsPage';

// Token Reward Animation
import TokenRewardAnimation from './components/tokens/TokenRewardAnimation';

// Network Resilience
import ErrorBoundary from './components/common/ErrorBoundary';
import NetworkStatusBar from './components/common/NetworkStatusBar';
import ToastContainer from './components/common/ToastContainer';

// Check if current path is an auth page (no navigation needed)
const isAuthPage = (pathname: string) => {
  const authPaths = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email', '/auth/callback', '/auth/consent'];
  return authPaths.some(path => pathname.startsWith(path));
};

// Check if current path is an admin page (admin has its own sidebar layout)
const isAdminPage = (pathname: string) => pathname.startsWith('/admin');

// Check if current path is a learning page (hide main nav to maximize content space)
const isLearningPage = (pathname: string) => {
  const learningModes = ['comprehension', 'vocabulary', 'dictation', 'shadowing', 'quiz'];
  return learningModes.some(mode => pathname.includes(`/courses/`) && pathname.endsWith(`/${mode}`));
};

// Navigation component with mode awareness and auth state
const Navigation: React.FC = () => {
  const { t } = useTranslation(['nav', 'common']);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const isCreatorMode = location.pathname.startsWith('/creator');
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileMenuMode, setMobileMenuMode] = useState<'learn' | 'create'>(isCreatorMode ? 'create' : 'learn');
  const menuRef = useRef<HTMLDivElement>(null);
  const navLinksRef = useRef<HTMLDivElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const [isCreatorDropdownOpen, setIsCreatorDropdownOpen] = useState(false);
  const creatorDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      // Close mobile menu when clicking outside
      if (
        isMobileMenuOpen &&
        navLinksRef.current &&
        !navLinksRef.current.contains(event.target as Node) &&
        hamburgerRef.current &&
        !hamburgerRef.current.contains(event.target as Node)
      ) {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen]);

  // Close mobile menu on route change & sync mode
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setMobileMenuMode(location.pathname.startsWith('/creator') ? 'create' : 'learn');
  }, [location.pathname]);

  // Close creator dropdown when clicking outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (creatorDropdownRef.current && !creatorDropdownRef.current.contains(e.target as Node)) {
        setIsCreatorDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Don't render navigation on auth pages
  if (isAuthPage(location.pathname)) {
    return null;
  }

  const handleLogout = async () => {
    setIsUserMenuOpen(false);
    await logout();
    navigate('/login');
  };

  // Helper: check if a path is active
  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  // Trial banner is rendered as a SIBLING of <nav> (inside <header>) so it
  // owns its own row at full viewport width. When it lived inside <nav>'s
  // flex layout it shrank to content width and looked truncated on mobile.
  const trialBanner = isAuthenticated && user?.subscriptionStatus === 'trialing' && user?.trialEndsAt
    ? (() => {
        const daysLeft = Math.max(0, Math.ceil((new Date(user.trialEndsAt!).getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
        const variant = daysLeft > 3 ? 'info' : daysLeft <= 1 ? 'urgent' : 'warning';
        const labelKey = daysLeft === 0
          ? 'trialEndsToday'
          : daysLeft <= 3
            ? 'trialEndsSoon'
            : 'trialRunning';
        return (
          <div className={`trial-banner-nav trial-banner-nav--${variant}`}>
            <span>
              {daysLeft === 0
                ? t(labelKey)
                : t(labelKey, { count: daysLeft })}
            </span>
            <Link to="/pricing" className="trial-banner-nav__cta">{t('upgradeNow')}</Link>
          </div>
        );
      })()
    : null;

  return (
    <>
    <nav className="main-nav">
      {/* Mobile hamburger — hidden on desktop */}
      <button
        ref={hamburgerRef}
        className={`nav-hamburger ${isMobileMenuOpen ? 'open' : ''}`}
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label={t('common:toggleMenu')}
        aria-expanded={isMobileMenuOpen}
      >
        <span />
        <span />
        <span />
      </button>

      {/* Brand */}
      <div className="nav-brand">
        <Link to="/">
          <LoopyMark size={24} />
          <span>Loopy AI</span>
        </Link>
      </div>

      {/* Desktop inline nav links */}
      <div className="nav-desktop-links">
        <Link to="/courses" className={`nav-link ${isActive('/courses') && !isCreatorMode ? 'nav-link--active' : ''}`}>
          {t('browseCourses')}
        </Link>
        <Link to="/topics" className={`nav-link ${isActive('/topics') ? 'nav-link--active' : ''}`}>
          {t('browseByTopic')}
        </Link>
        <Link to="/chosen-courses" className={`nav-link ${isActive('/chosen-courses') ? 'nav-link--active' : ''}`}>
          {t('chosenCourses')}
        </Link>
        <Link to="/review" className={`nav-link ${isActive('/review') ? 'nav-link--active' : ''}`}>
          {t('review')}
        </Link>
        <Link to="/vocabulary-book" className={`nav-link ${isActive('/vocabulary-book') ? 'nav-link--active' : ''}`}>
          {t('vocabularyBook')}
        </Link>
        <Link to="/dictionary" className={`nav-link ${isActive('/dictionary') ? 'nav-link--active' : ''}`}>
          {t('dictionary')}
        </Link>
        <Link to="/placement-test" className={`nav-link ${isActive('/placement-test') ? 'nav-link--active' : ''}`}>
          {t('levelTest')}
        </Link>

        {/* Creator dropdown */}
        <div className="nav-creator-dropdown" ref={creatorDropdownRef}>
          <button
            className={`nav-link nav-link--dropdown ${isCreatorMode ? 'nav-link--active' : ''}`}
            onClick={() => setIsCreatorDropdownOpen(!isCreatorDropdownOpen)}
          >
            {t('creator')}
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <path d="M2.5 4L5 6.5L7.5 4" />
            </svg>
          </button>
          {isCreatorDropdownOpen && (
            <div className="nav-creator-menu">
              <Link to="/creator/courses" className="nav-creator-menu__item" onClick={() => setIsCreatorDropdownOpen(false)}>
                {t('myCourses')}
              </Link>
              <Link to="/creator/series" className="nav-creator-menu__item" onClick={() => setIsCreatorDropdownOpen(false)}>
                {t('mySeries')}
              </Link>
              <Link to="/creator/learner-questions" className="nav-creator-menu__item" onClick={() => setIsCreatorDropdownOpen(false)}>
                {t('learnerQuestionsNav')}
              </Link>
              <Link to="/creator/wizard" className="nav-creator-menu__item nav-creator-menu__item--cta" onClick={() => setIsCreatorDropdownOpen(false)}>
                {t('newCourse')}
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Right-side utilities */}
      <div className="nav-auth">
        {isAuthenticated && <NotificationBell />}
        {isAuthenticated && user ? (
          <div className="nav-user-dropdown" ref={menuRef}>
            <button
              className="nav-user-btn"
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            >
              {user.avatarUrl ? (
                <img src={user.avatarUrl} alt="" className="nav-user-avatar" />
              ) : (
                <span className="nav-user-avatar-placeholder">
                  {(user.fullName || user.username || 'U').charAt(0).toUpperCase()}
                </span>
              )}
              <span className="nav-user-name">{user.fullName || user.username}</span>
              <svg className={`nav-user-chevron ${isUserMenuOpen ? 'open' : ''}`} width="12" height="12" viewBox="0 0 12 12">
                <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
            </button>
            {isUserMenuOpen && (
              <div className="nav-user-menu-dropdown">
                <Link to="/profile" className="nav-menu-item" onClick={() => setIsUserMenuOpen(false)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  <span>{t('profile')}</span>
                </Link>
                <Link to="/messages" className="nav-menu-item" onClick={() => setIsUserMenuOpen(false)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                  <span>{t('messages')}</span>
                </Link>
                <Link to="/progression" className="nav-menu-item" onClick={() => setIsUserMenuOpen(false)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 20V10"/><path d="M18 20V4"/><path d="M6 20v-4"/>
                  </svg>
                  <span>{t('progression')}</span>
                </Link>
                <Link to="/knowledge-graph" className="nav-menu-item" onClick={() => setIsUserMenuOpen(false)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="3"/><path d="M12 2v4"/><path d="M12 18v4"/><path d="M4.93 4.93l2.83 2.83"/><path d="M16.24 16.24l2.83 2.83"/><path d="M2 12h4"/><path d="M18 12h4"/><path d="M4.93 19.07l2.83-2.83"/><path d="M16.24 7.76l2.83-2.83"/>
                  </svg>
                  <span>{t('knowledgeGraph')}</span>
                </Link>
                <Link to="/reports" className="nav-menu-item" onClick={() => setIsUserMenuOpen(false)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="8" y1="13" x2="16" y2="13"/>
                    <line x1="8" y1="17" x2="13" y2="17"/>
                  </svg>
                  <span>{t('weeklyReport')}</span>
                </Link>
                <Link to="/writing" className="nav-menu-item" onClick={() => setIsUserMenuOpen(false)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="12,2 15,8.5 22,9 17,14 18.5,21 12,17.5 5.5,21 7,14 2,9 9,8.5"/>
                  </svg>
                  <span>{t('writing')}</span>
                </Link>
                <Link to="/notes" className="nav-menu-item" onClick={() => setIsUserMenuOpen(false)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2z"/>
                    <line x1="8" y1="8" x2="16" y2="8"/>
                    <line x1="8" y1="12" x2="16" y2="12"/>
                    <line x1="8" y1="16" x2="13" y2="16"/>
                  </svg>
                  <span>{t('myNotes')}</span>
                </Link>
                <Link to="/wishlist" className="nav-menu-item" onClick={() => setIsUserMenuOpen(false)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 18l6-6-6-6"/>
                    <path d="M4 12h12"/>
                    <rect x="2" y="3" width="20" height="18" rx="2"/>
                  </svg>
                  <span>{t('wishlist')}</span>
                </Link>
                <Link to="/referral" className="nav-menu-item" onClick={() => setIsUserMenuOpen(false)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                  <span>{t('inviteFriends')}</span>
                </Link>
                <button onClick={handleLogout} className="nav-menu-item nav-menu-logout">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                    <polyline points="16,17 21,12 16,7"/>
                    <line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                  {t('logout')}
                </button>
              </div>
            )}
          </div>
        ) : (
          <Link to="/login" className="nav-login-btn">{t('login')}</Link>
        )}
      </div>

      {/* Mobile dropdown menu */}
      <div
        ref={navLinksRef}
        className={`nav-mobile-menu ${isMobileMenuOpen ? 'nav-mobile-menu--open' : ''}`}
      >
        <div className="nav-mobile-menu__mode-tabs">
          <button
            className={`nav-mobile-menu__mode-tab ${mobileMenuMode === 'learn' ? 'active' : ''}`}
            onClick={() => setMobileMenuMode('learn')}
          >
            {t('learn', 'Learn')}
          </button>
          <button
            className={`nav-mobile-menu__mode-tab ${mobileMenuMode === 'create' ? 'active' : ''}`}
            onClick={() => setMobileMenuMode('create')}
          >
            {t('create', 'Create')}
          </button>
        </div>
        {mobileMenuMode === 'create' ? (
          <>
            <Link to="/creator/courses" onClick={() => setIsMobileMenuOpen(false)}>{t('myCourses')}</Link>
            <Link to="/creator/series" onClick={() => setIsMobileMenuOpen(false)}>{t('mySeries')}</Link>
            <Link to="/creator/learner-questions" onClick={() => setIsMobileMenuOpen(false)}>{t('learnerQuestionsNav')}</Link>
            <Link to="/creator/wizard" onClick={() => setIsMobileMenuOpen(false)}>{t('newCourse')}</Link>
          </>
        ) : (
          <>
            <Link to="/courses" onClick={() => setIsMobileMenuOpen(false)}>{t('browseCourses')}</Link>
            <Link to="/topics" onClick={() => setIsMobileMenuOpen(false)}>{t('browseByTopic')}</Link>
            <Link to="/chosen-courses" onClick={() => setIsMobileMenuOpen(false)}>{t('chosenCourses')}</Link>
            <Link to="/writing" onClick={() => setIsMobileMenuOpen(false)}>{t('writing')}</Link>
            <Link to="/playlists" onClick={() => setIsMobileMenuOpen(false)}>{t('playlists')}</Link>
            <Link to="/wishlist" onClick={() => setIsMobileMenuOpen(false)}>{t('wishlist')}</Link>
            <Link to="/notes" onClick={() => setIsMobileMenuOpen(false)}>{t('myNotes')}</Link>
            <Link to="/review" onClick={() => setIsMobileMenuOpen(false)}>{t('review')}</Link>
            <Link to="/vocabulary-book" onClick={() => setIsMobileMenuOpen(false)}>{t('vocabularyBook')}</Link>
          </>
        )}
        <div className="nav-mobile-menu__divider" />
        <Link to="/dictionary" onClick={() => setIsMobileMenuOpen(false)}>{t('dictionary')}</Link>
        <Link to="/placement-test" onClick={() => setIsMobileMenuOpen(false)}>{t('levelTest')}</Link>
        <Link to="/pricing" onClick={() => setIsMobileMenuOpen(false)}>{t('pricing')}</Link>
      </div>
    </nav>
    {/* Trial banner sits OUTSIDE the .main-nav flex layout so it can claim
        a full viewport-width row. Three escalation states applied via the
        variant class — see App.css `.trial-banner-nav--info/warning/urgent`. */}
    {trialBanner}
    </>
  );
};

const WELCOME_TRIAL_FLAG = 'welcomeTrialSeen';
// Only show WelcomeTrialModal to truly fresh registrations. Users who created
// their account before this feature shipped should NOT see the modal on a
// new device just because the localStorage flag is missing.
const WELCOME_TRIAL_FRESH_MS = 10 * 60 * 1000; // 10 min

function AppContent() {
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const { pendingRewards, showRewardAnimation, onRewardsClaimed, dismissRewardAnimation } = useTokens();
  const { connect: connectNotifications, disconnect: disconnectNotifications } = useNotificationStore();

  const [showWelcomeTrial, setShowWelcomeTrial] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    if (user.subscriptionStatus !== 'trialing') return;
    if (localStorage.getItem(WELCOME_TRIAL_FLAG) === '1') return;
    const createdAt = user.createdAt ? new Date(user.createdAt).getTime() : 0;
    if (!createdAt || Date.now() - createdAt > WELCOME_TRIAL_FRESH_MS) return;
    setShowWelcomeTrial(true);
  }, [isAuthenticated, user]);

  const handleCloseWelcomeTrial = () => {
    localStorage.setItem(WELCOME_TRIAL_FLAG, '1');
    setShowWelcomeTrial(false);
  };

  // Google Analytics 4 page tracking
  usePageTracking();

  // Keep <title> + <meta description> in sync with i18n language
  useDocumentMeta();

  // Connect to notification WebSocket when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      connectNotifications();
    } else {
      disconnectNotifications();
    }

    return () => {
      disconnectNotifications();
    };
  }, [isAuthenticated, user, connectNotifications, disconnectNotifications]);

  // Auth pages: redirect authenticated users to home, never show login/register forms.
  // AuthBootstrapGate guarantees isAuthenticated is verified before this renders,
  // so we no longer need to peek at localStorage to avoid flashes.
  if (isAuthPage(location.pathname)) {
    const guestOnlyPaths = ['/login', '/register', '/forgot-password'];
    const isGuestOnlyPage = guestOnlyPaths.some(p => location.pathname.startsWith(p));

    if (isGuestOnlyPage && isAuthenticated) {
      return <Navigate to="/" replace />;
    }

    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/auth/consent" element={<ParentalConsentPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
      </Routes>
    );
  }

  return (
    <div className="App">
      {/* Hide main nav in learning mode and admin mode */}
      {!isLearningPage(location.pathname) && !isAdminPage(location.pathname) && (
        <header className="App-header">
          <Navigation />
        </header>
      )}

      {/* Email Verification Banner */}
      {isAuthenticated && user && !user.emailVerified && !isLearningPage(location.pathname) && (
        <EmailVerificationBanner email={user.email} />
      )}

      <main>
        <Routes>
          {/* Home */}
          <Route path="/" element={<HomePage />} />

          {/* Welcome - Referral Landing Page */}
          <Route path="/welcome" element={<WelcomePage />} />

          {/* Pricing */}
          <Route path="/pricing" element={<PricingPage />} />
          <Route
            path="/subscription/success"
            element={
              <ProtectedRoute>
                <SubscriptionSuccessPage />
              </ProtectedRoute>
            }
          />
          <Route path="/cefr-guide" element={<CEFRGuidePage />} />

          {/* Creator Program — page is hidden until revenue-share system
              ships. Component file kept at pages/CreatorProgramPage.tsx so we
              can re-mount the route when payouts go live. */}

          {/* Legal Pages */}
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />

          {/* About & Blog */}
          <Route path="/about" element={<AboutPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/blog" element={<BlogListPage />} />
          <Route path="/blog/:slug" element={<BlogDetailPage />} />

          {/* Admin Routes */}
          <Route path="/admin" element={
            <ProtectedRoute requiredRoles={['admin', 'support']}>
              <AdminLayout />
            </ProtectedRoute>
          }>
            <Route index element={<AdminDashboardPage />} />
            <Route path="subscriptions" element={<AdminSubscriptionsPage />} />
            <Route path="subscriptions/:id" element={<AdminSubscriptionDetailPage />} />
            <Route path="transactions" element={<AdminTransactionsPage />} />
            <Route path="revenue" element={<AdminRevenuePage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="users/:id" element={<AdminUserDetailPage />} />
            <Route path="churn" element={<AdminChurnPage />} />
            <Route path="learning-time" element={<AdminLearningTimePage />} />
            <Route path="creator-contributions" element={<AdminCreatorContributionsPage />} />
            <Route path="audit-log" element={<AdminAuditLogPage />} />
            <Route path="email-hub" element={<AdminEmailHubPage />} />
          </Route>

          {/* Profile (Protected) */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />

          {/* Parental Controls Dashboard (COPPA / GDPR-K) */}
          <Route
            path="/account/parental-controls"
            element={
              <ProtectedRoute>
                <ParentalControlsPage />
              </ProtectedRoute>
            }
          />

          {/* Dictionary Routes */}
          <Route path="/dictionary/zh-tw" element={<Navigate to="/dictionary" replace />} />
          <Route path="/dictionary/zh-cn" element={<Navigate to="/dictionary" replace />} />
          <Route path="/dictionary" element={<DictionaryPage />} />

          {/* Creator Routes — require active subscription or trial (admin bypass) */}
          <Route path="/creator/drafts" element={<Navigate to="/creator/courses?tab=drafts" replace />} />
          <Route
            path="/creator/analytics"
            element={
              <ProtectedRoute>
                <SubscriptionGate feature="Creator Mode">
                  <CreatorAnalyticsPage />
                </SubscriptionGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/learner-questions"
            element={
              <ProtectedRoute>
                <SubscriptionGate feature="Creator Mode">
                  <CreatorLearnerQuestionsPage />
                </SubscriptionGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/courses"
            element={
              <ProtectedRoute>
                <SubscriptionGate feature="Creator Mode">
                  <CreatorCoursesPage />
                </SubscriptionGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/groups"
            element={
              <ProtectedRoute>
                <SubscriptionGate feature="Creator Mode">
                  <CreatorGroupsPage />
                </SubscriptionGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/courses/:courseId"
            element={
              <ProtectedRoute>
                <SubscriptionGate feature="Creator Mode">
                  <CourseDetailPage />
                </SubscriptionGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/wizard"
            element={
              <ProtectedRoute>
                <SubscriptionGate feature="Creator Mode">
                  <CreatorWizardPage />
                </SubscriptionGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/wizard/:draftId"
            element={
              <ProtectedRoute>
                <SubscriptionGate feature="Creator Mode">
                  <CreatorWizardPage />
                </SubscriptionGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/wizard/:draftId/subtitles"
            element={
              <ProtectedRoute>
                <SubscriptionGate feature="Creator Mode">
                  <SubtitleEditorPage />
                </SubscriptionGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/subtitle-editor/:draftId"
            element={
              <ProtectedRoute>
                <SubscriptionGate feature="Creator Mode">
                  <SubtitleEditorPage />
                </SubscriptionGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/series"
            element={
              <ProtectedRoute>
                <SubscriptionGate feature="Creator Mode">
                  <MySeriesPage />
                </SubscriptionGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/series/:id"
            element={
              <ProtectedRoute>
                <SubscriptionGate feature="Creator Mode">
                  <SeriesDetailPage />
                </SubscriptionGate>
              </ProtectedRoute>
            }
          />

          {/* Learning Test Routes */}
          <Route path="/learning/test" element={<LearningAPITestPage />} />
          <Route path="/learning/fulltext" element={<FullTextTestPage />} />

          {/* Course Routes */}
          <Route path="/topics" element={<TopicsPage />} />
          <Route path="/courses" element={<CourseListPage />} />
          <Route path="/courses/:courseId" element={<CourseDetailPage />} />
          <Route
            path="/chosen-courses"
            element={
              <ProtectedRoute>
                <MyCoursesPage />
              </ProtectedRoute>
            }
          />
          <Route path="/my-courses" element={<Navigate to="/chosen-courses" replace />} />

          {/* Standalone Writing Routes */}
          <Route
            path="/writing"
            element={
              <ProtectedRoute>
                <WritingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/writing/history"
            element={
              <ProtectedRoute>
                <WritingHistoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/writing/history/:sessionId"
            element={
              <ProtectedRoute>
                <WritingHistoryDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/writing/:gateType"
            element={
              <ProtectedRoute>
                <StandaloneGatePage />
              </ProtectedRoute>
            }
          />

          {/* Legacy /quest redirects (preserve existing bookmarks/links) */}
          <Route path="/quest" element={<Navigate to="/writing" replace />} />
          <Route path="/quest/history" element={<Navigate to="/writing/history" replace />} />
          <Route path="/quest/history/:sessionId" element={<Navigate to="/writing/history/:sessionId" replace />} />
          <Route path="/quest/:gateType" element={<Navigate to="/writing/:gateType" replace />} />

          {/* Creator Writing Questions Editor */}
          <Route
            path="/creator/courses/:courseId/writing-questions"
            element={
              <ProtectedRoute>
                <CourseWritingQuestionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/creator/courses/:courseId/quest-questions"
            element={<Navigate to="/creator/courses/:courseId/writing-questions" replace />}
          />

          {/* Course Writing Routes (before LearningLayout wildcard) */}
          <Route
            path="/courses/:courseId/writing"
            element={
              <ProtectedRoute>
                <CourseWritingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/courses/:courseId/writing/:gateType"
            element={
              <ProtectedRoute>
                <GatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/courses/:courseId/quest"
            element={<Navigate to="/courses/:courseId/writing" replace />}
          />
          <Route
            path="/courses/:courseId/quest/:gateType"
            element={<Navigate to="/courses/:courseId/writing/:gateType" replace />}
          />

          {/* Learning Mode Routes — tiered freemium gating */}
          <Route path="/courses/:courseId/*" element={<LearningLayout />}>
            <Route path="comprehension" element={<TieredGate tier={1} feature="Comprehension"><ComprehensionPage /></TieredGate>} />
            <Route path="vocabulary" element={<TieredGate tier={2} featureType="vocab_flashcard" feature="Vocabulary"><VocabularyPage /></TieredGate>} />
            <Route path="dictation" element={<TieredGate tier={2} featureType="dictation" feature="Dictation"><DictationPage /></TieredGate>} />
            <Route path="shadowing" element={<TieredGate tier={2} featureType="shadowing" feature="Shadowing"><ShadowingPage /></TieredGate>} />
            <Route path="quiz" element={<TieredGate tier={2} featureType="quiz" feature="Quiz"><QuizPage /></TieredGate>} />
          </Route>

          {/* Notes — Tier 2 (watch ad → today's note allowance) */}
          <Route path="/notes" element={<TieredGate tier={2} featureType="notes" feature="Notes"><NotesPage /></TieredGate>} />

          {/* Vocabulary Book — Tier 2 (shares ad-unlock with /learning/.../vocabulary) */}
          <Route path="/vocabulary-book" element={<TieredGate tier={2} featureType="vocab_flashcard" feature="Vocabulary Book"><VocabularyBookPage /></TieredGate>} />
          <Route path="/vocabulary-book/practice" element={<TieredGate tier={2} featureType="vocab_flashcard" feature="Flashcard Practice"><FlashcardPracticePage /></TieredGate>} />

          {/* Tokens Page */}
          <Route
            path="/tokens"
            element={
              <ProtectedRoute>
                <TokensPage />
              </ProtectedRoute>
            }
          />

          {/* Referral Page */}
          <Route
            path="/referral"
            element={
              <ProtectedRoute>
                <ReferralPage />
              </ProtectedRoute>
            }
          />

          {/* Messages Page */}
          <Route
            path="/messages"
            element={
              <ProtectedRoute>
                <MessagesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages/:conversationId"
            element={
              <ProtectedRoute>
                <MessagesPage />
              </ProtectedRoute>
            }
          />

          {/* Notifications Page */}
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <NotificationsPage />
              </ProtectedRoute>
            }
          />

          {/* Public Profile Page */}
          <Route path="/profile/:userId" element={<PublicProfilePage />} />

          {/* Playlist Routes */}
          <Route
            path="/playlists"
            element={
              <ProtectedRoute>
                <MyPlaylistsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/playlists/:id"
            element={
              <ProtectedRoute>
                <PlaylistDetailPage />
              </ProtectedRoute>
            }
          />

          {/* Wishlist */}
          <Route path="/wishlist" element={<WishlistPage />} />

          {/* Test Pages */}
          <Route path="/test/underline" element={<UnderlineTestPage />} />
          <Route path="/test/syntax-tree" element={<SyntaxTreeDemoPage />} />
          <Route path="/test/progression" element={<ProgressionPreview />} />
          <Route path="/test/translation-eval" element={<TranslationEvalTestPage />} />
          <Route path="/test/model-compare" element={<ModelComparePage />} />
          <Route path="/test/tts-compare" element={<TTSComparePage />} />
          <Route path="/test/tts-temperature" element={<TTSTemperaturePage />} />
          <Route path="/test/silence-experiment" element={<SilenceExperimentPage />} />
          <Route path="/test/intonation-compare" element={<IntonationComparePage />} />
          <Route path="/test/episode-selector" element={<EpisodeSelectorTestPage />} />
          <Route path="/test/speech-input" element={<SpeechInputTestPage />} />
          <Route path="/test/shadowing-experiment" element={<ShadowingExperimentPage />} />
          <Route path="/test/asr-compare" element={<AsrComparePage />} />
          <Route path="/test/annotation-parser" element={<AnnotationParserTestPage />} />
          <Route path="/test/cefr-tree" element={<CEFRTreePage />} />
          <Route path="/test/learning-journey" element={<LearningJourneyPage />} />
          <Route path="/test/syntax-compare" element={<SyntaxComparePage />} />

          {/* Review Center (replaces Learning Journey) */}
          <Route path="/review" element={<ProtectedRoute><ReviewCenterPage /></ProtectedRoute>} />
          {/* Backward-compat: redirect old /learning-journey to /review */}
          <Route path="/learning-journey" element={<Navigate to="/review" replace />} />

          {/* Progression Page */}
          <Route
            path="/progression"
            element={
              <ProtectedRoute>
                <ProgressionPage />
              </ProtectedRoute>
            }
          />

          {/* Knowledge Graph */}
          <Route
            path="/knowledge-graph"
            element={
              <ProtectedRoute>
                <KnowledgeGraphPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/knowledge-graph/caffa"
            element={
              <ProtectedRoute>
                <CaffaExplainerPage />
              </ProtectedRoute>
            }
          />

          {/* Weekly Learning Reports */}
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <LearningReportsListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/:id"
            element={
              <ProtectedRoute>
                <LearningReportDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/knowledge/phonics"
            element={
              <ProtectedRoute>
                <PhonicsReferencePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/knowledge/morphology"
            element={
              <ProtectedRoute>
                <MorphologyReferencePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/knowledge/prosody"
            element={
              <ProtectedRoute>
                <ProsodyReferencePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/knowledge/sight-words"
            element={
              <ProtectedRoute>
                <SightWordsReferencePage />
              </ProtectedRoute>
            }
          />

          {/* Placement Test (public access for anonymous testing) */}
          <Route path="/placement-test" element={<PlacementTestPage />} />
          <Route
            path="/placement-test/result/:sessionId"
            element={
              <ProtectedRoute>
                <PlacementTestResultPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/placement-test/history"
            element={
              <ProtectedRoute>
                <PlacementTestHistoryPage />
              </ProtectedRoute>
            }
          />

        </Routes>
      </main>

      {!isLearningPage(location.pathname) && !isAdminPage(location.pathname) && !location.pathname.startsWith('/messages') && <Footer />}
      <CookieConsent />

      {/* Token Reward Animation (for referral rewards) */}
      {showRewardAnimation && pendingRewards.length > 0 && (
        <TokenRewardAnimation
          pendingRewards={pendingRewards}
          onComplete={onRewardsClaimed}
          onClose={dismissRewardAnimation}
        />
      )}

      {showWelcomeTrial && <WelcomeTrialModal onClose={handleCloseWelcomeTrial} />}
    </div>
  );
}

function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ScrollToTop />
      <ErrorBoundary>
        <AuthProvider>
          <AuthBootstrapGate>
            <TokenProvider>
              <LevelUpProvider>
                <FreeLimitProvider>
                  <NetworkStatusBar />
                  <AppContent />
                  <ToastContainer />
                </FreeLimitProvider>
              </LevelUpProvider>
            </TokenProvider>
          </AuthBootstrapGate>
        </AuthProvider>
      </ErrorBoundary>
    </Router>
  );
}

export default App;
